'use client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react'
import _ from 'lodash'
// Utils
import {
  findBallotIdByOrgSlugAndBallotSlug,
  findWebsite,
  getBallotById,
} from '../lib/api'
import {logDataByLevel} from '../utils/logging-helpers'
import {removeTrailingSlash} from '../utils/string-helpers'
// Consts
import {INPUT_DEBOUNCE_MS} from '../consts/timing'
// Types
import {Ballot} from '../types/ballot'
import {Website} from '../types/website'

interface BallotContextValue {
  basicBallot: Ballot | null
  fullBallot: Ballot | null
  setFullBallot: Dispatch<SetStateAction<Ballot | null>>
  isLoadingFullBallot: boolean
}

interface BallotProviderProps {
  children: ReactNode
  initBasicBallot?: Ballot | null
}

interface BallotIdFromHostResult {
  ballotId: string | null
  error: string | null
  skipAuthDomain?: boolean
}

const BallotContext = createContext<BallotContextValue | undefined>(undefined)

const getBallotIdFromHost = async (host: string): Promise<BallotIdFromHostResult> => {
  // Strip www. prefix for consistent preview domain matching
  const cleanHost = host.replace(/^www\./, '')

  // Check if we're on the bare preview domain itself (no subdomain)
  const isBarePreviewDomain = (
    cleanHost === 'photocontest.vote'
    || cleanHost === 'photocontestbeta.dev'
  )

  if (isBarePreviewDomain) {
    // On the bare preview domain, check for /o/ path to extract org and ballot slugs
    const pathMatch = window.location.pathname.match(/^\/o\/([^/]+)\/([^/]+)/)
    if (pathMatch) {
      const ballotId = await findBallotIdByOrgSlugAndBallotSlug(
        pathMatch[1],
        pathMatch[2],
      )
      if (!ballotId) return {ballotId: null, error: 'This preview ballot could not be found'}
      return {error: null, ballotId}
    }
    return {ballotId: null, error: 'This ballot does not currently have a preview enabled'}
  }

  const splitHost = cleanHost.split('.')
  const domain = _.join(_.tail(splitHost), '.')

  let ballotId: string | null = null
  if (
    domain === 'photocontest.vote'
    || domain === 'photocontestbeta.dev'
  ) {
    const subdomain = _.first(splitHost)
    const splitSubdomain = subdomain?.split('--')

    if (!splitSubdomain || splitSubdomain.length !== 2) {
      return {ballotId: null, error: `Invalid subdomain format for photocontest.vote or photocontestbeta.dev: ${subdomain}`}
    }

    ballotId = await findBallotIdByOrgSlugAndBallotSlug(
      _.last(splitSubdomain)!,
      _.first(splitSubdomain)!,
    )
    if (!ballotId) return {ballotId: null, error: 'This preview ballot could not be found'}

    return {error: null, ballotId}
  }

  // Use original host for website lookup since registered URLs may include www
  const foundWebsite: Website | null = await findWebsite(removeTrailingSlash(host))
  if (foundWebsite) {
    return {error: null, ballotId: foundWebsite.ballot_id ?? null}
  } else {
    return {error: `No matching website found, ${host}`, ballotId: null}
  }
}

export const BallotConsumer = BallotContext.Consumer

export const BallotProvider = ({children, initBasicBallot = null}: BallotProviderProps) => {
  const [basicBallot, setBasicBallot] = useState<Ballot | null>(initBasicBallot)
  const [fullBallot, setFullBallot] = useState<Ballot | null>(null)
  const [isLoadingFullBallot, setIsLoadingFullBallot] = useState<boolean>(true)

  const handleError = (message: string, error: any): null => {
    setIsLoadingFullBallot(false)
    logDataByLevel({
      level: 'error',
      message: message,
      data: error,
    })
    return null
  }

  const loadFullBallot = useCallback(async () => {
    if (initBasicBallot) {
      if (
        !fullBallot
        || (
          fullBallot.ballot_updated_date_time
          && (
            new Date(fullBallot.ballot_updated_date_time).getTime()
            !== new Date(initBasicBallot.ballot_updated_date_time).getTime()
          )
        )
      ) {
        try {
          const newBallot = await getBallotById(initBasicBallot._id)
          setFullBallot(newBallot)
        } catch (error) {
          handleError(
            'Error getting ballot by id 01',
            {
              basic_id: initBasicBallot._id,
              full_id: fullBallot?._id,
              basic_updated: initBasicBallot.ballot_updated_date_time,
              full_updated: fullBallot?.ballot_updated_date_time,
              error: error,
            },
          )
        }
      }
    }
    if (!initBasicBallot) {
      try {
        const {error, ballotId, skipAuthDomain} = await getBallotIdFromHost(window.location.host)
        if (skipAuthDomain) {
          // Auth/login host: intentionally no ballot; stop silently.
          return
        }
        if (error) {
          handleError('Error getting ballot from host', error)
          return
        }
        if (!ballotId) return
        const newBallot = await getBallotById(ballotId).catch((error: any) => handleError('Error getting ballot by id 02', error))
        setFullBallot(newBallot)
      } catch (error) {
        handleError('Error getting ballot by id 03', error)
      }
    }
  }, [initBasicBallot, fullBallot])

  const debouncedLoadFullBallot = useMemo(() => _.debounce(async () => {
    setIsLoadingFullBallot(true)
    await loadFullBallot()
    setIsLoadingFullBallot(false)
  }, INPUT_DEBOUNCE_MS), [loadFullBallot])

  useEffect(() => {
    setBasicBallot(initBasicBallot)

    debouncedLoadFullBallot()
    return () => {
      debouncedLoadFullBallot.cancel()
    }
  }, [initBasicBallot, debouncedLoadFullBallot])

  const contextValue = useMemo(() => ({
    basicBallot,
    fullBallot,
    setFullBallot,
    isLoadingFullBallot,
  }), [
    basicBallot,
    fullBallot,
    isLoadingFullBallot,
  ])

  return (
    <BallotContext.Provider value={contextValue}>
      {children}
    </BallotContext.Provider>
  )
}

export const useBallotContext = (): BallotContextValue => {
  const context = useContext(BallotContext)
  if (!context) {
    throw new Error('useBallotContext must be used within a BallotProvider')
  }
  return context
}
